import type { RemoteThreadListAdapter } from "@assistant-ui/react";
import { createAssistantStream } from "assistant-stream";
import { useMemo } from "react";
import { useApi } from "@/lib/api-client";
import { threadIdFor } from "@/lib/thread-id";
import { isPinned, type ThreadDto, titleConversation, toThreadMetadata } from "@/lib/threads";

/**
 * assistant-ui's thread list backed by the chat API's thread index: every action (rename, pin,
 * archive, delete, generated titles) is stored server side, so it holds across reloads and devices.
 */
export function useRemoteThreadAdapter() {
  const client = useApi();

  return useMemo(() => {
    const threads = client.api.chat.threads;
    const patch = async (
      id: string,
      json: { title?: string; pinned?: boolean; archived?: boolean },
    ) => {
      const res = await threads[":id"].$patch({ param: { id }, json });
      if (!res.ok) throw new Error(`Couldn't update the chat (${res.status})`);
    };

    const adapter: RemoteThreadListAdapter = {
      async list() {
        // Archived threads too: assistant-ui keeps them apart for the archived view.
        const res = await threads.$get({ query: { status: "all" } });
        if (!res.ok) return { threads: [] };
        const rows = (await res.json()) as ThreadDto[];
        return { threads: rows.map(toThreadMetadata) };
      },
      async delete(threadId) {
        await threads[":id"].$delete({ param: { id: threadId } });
      },
      async rename(threadId, title) {
        await patch(threadId, { title });
      },
      async updateCustom(threadId, custom) {
        await patch(threadId, { pinned: isPinned(custom) });
      },
      async archive(threadId) {
        await patch(threadId, { archived: true });
      },
      async unarchive(threadId) {
        await patch(threadId, { archived: false });
      },
      async initialize(threadId) {
        // The id the thread's ChatAgent already runs under (use-agent-thread.ts); its first
        // message files it in the thread index.
        const id = threadIdFor(threadId);
        return { remoteId: id, externalId: id };
      },
      async fetch(threadId) {
        const res = await threads[":id"].$get({ param: { id: threadId } });
        if (!res.ok) throw new Error("Thread not found");
        return toThreadMetadata((await res.json()) as ThreadDto);
      },
      // A small model names the thread from the first question and answer; the title streams in
      // as plain text and is stored before the stream ends. Until the first answer has text this
      // returns an empty stream, and assistant-ui asks again once the run ends.
      async generateTitle(threadId, messages) {
        const conversation = titleConversation(messages);
        return createAssistantStream(async (controller) => {
          if (!conversation) return;
          const res = await threads[":id"].title.$post({
            param: { id: threadId },
            json: { messages: conversation },
          });
          if (!res.ok || !res.body) return;
          const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
          for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            if (value) controller.appendText(value);
          }
        });
      },
    };
    return adapter;
  }, [client]);
}
